import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * WHATSAPP-STYLE TEXT (notes, remarks).
 *
 *   *bold*   _italic_   ~strike~   `code`   ```monospace```   > quote
 *
 * Line breaks, blank lines and indentation render exactly as typed. Notes are
 * stored verbatim; the old `<p>{note}</p>` collapsed every line of a
 * structured note ("1. Academic Profile / • 10th/SSLC …") into one run-on
 * paragraph. Markers follow WhatsApp's rule: they count only at word edges
 * (`snake_case_name` and `2*3*4` stay as typed) and never across a line.
 *
 * Builds React nodes only — no innerHTML — so a note can never inject markup;
 * links open for http(s) only.
 *
 * `inline`: ONE line for card / table previews — newlines become spaces,
 * fences and quotes are plain text, links are not clickable (the row itself
 * is the click target).
 */

type Marker = "*" | "_" | "~";

const TAG: Record<Marker, "strong" | "em" | "s"> = { "*": "strong", _: "em", "~": "s" };
const TAG_CLASS: Record<Marker, string> = {
  "*": "font-semibold",
  _: "italic",
  "~": "line-through",
};
const CODE_CLASS = "rounded bg-[#f1f3f2] px-1 py-px font-mono text-[0.92em]";
const PRE_CLASS =
  "my-1 whitespace-pre-wrap break-words rounded-md bg-[#f1f3f2] px-2 py-1.5 font-mono text-[0.92em]";
const QUOTE_CLASS = "block border-l-[3px] border-[#cdd1d0] pl-2 text-[#697571]";
const LINK_CLASS = "break-all text-[#1a73e8] underline underline-offset-2";

const isWordChar = (c: string | undefined) => !!c && /[\p{L}\p{N}]/u.test(c);
const isSpace = (c: string | undefined) => !c || /\s/.test(c);

/** Index of the marker closing the one opened at `open`, or -1. */
function findClose(text: string, open: number, m: Marker): number {
  // Content is at least one character, so the earliest close is open + 2.
  for (let j = open + 2; j < text.length; j++) {
    if (text[j] === m && !isSpace(text[j - 1]) && !isWordChar(text[j + 1])) return j;
  }
  return -1;
}

const URL_RE = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;

function linkify(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let n = 0;
  for (const match of text.matchAll(URL_RE)) {
    let url = match[0];
    // Sentence punctuation after a link is not part of it.
    const trail = url.match(/[.,;:!?)\]]+$/)?.[0] ?? "";
    if (trail) url = url.slice(0, -trail.length);
    const start = match.index ?? 0;
    if (start > last) out.push(text.slice(last, start));
    out.push(
      <a
        key={`${key}-l${n++}`}
        href={/^https?:\/\//i.test(url) ? url : `https://${url}`}
        target="_blank"
        rel="noopener noreferrer"
        className={LINK_CLASS}
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>,
    );
    last = start + url.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

/** One line of text → formatted nodes (markers may nest: `*_both_*`). */
function formatInline(text: string, key: string, links: boolean): ReactNode[] {
  const out: ReactNode[] = [];
  let buf = "";
  let n = 0;
  const flush = () => {
    if (!buf) return;
    if (links) out.push(...linkify(buf, `${key}-t${n++}`));
    else out.push(buf);
    buf = "";
  };

  let i = 0;
  while (i < text.length) {
    if (text.startsWith("```", i)) {
      const end = text.indexOf("```", i + 3);
      if (end > i + 3) {
        flush();
        out.push(
          <code key={`${key}-m${n++}`} className={CODE_CLASS}>
            {text.slice(i + 3, end)}
          </code>,
        );
        i = end + 3;
        continue;
      }
    }
    const c = text[i];
    if (c === "`") {
      const end = text.indexOf("`", i + 1);
      if (end > i + 1) {
        flush();
        out.push(
          <code key={`${key}-c${n++}`} className={CODE_CLASS}>
            {text.slice(i + 1, end)}
          </code>,
        );
        i = end + 1;
        continue;
      }
    }
    if ((c === "*" || c === "_" || c === "~") && !isWordChar(text[i - 1]) && !isSpace(text[i + 1])) {
      const close = findClose(text, i, c);
      if (close > -1) {
        flush();
        const Tag = TAG[c];
        const k = `${key}-f${n++}`;
        out.push(
          <Tag key={k} className={TAG_CLASS[c]}>
            {formatInline(text.slice(i + 1, close), k, links)}
          </Tag>,
        );
        i = close + 1;
        continue;
      }
    }
    buf += c;
    i++;
  }
  flush();
  return out;
}

interface FormattedTextProps {
  text: string | null | undefined;
  /** One line (card / table previews): newlines → spaces, no links. */
  inline?: boolean;
  className?: string;
}

export default function FormattedText({ text, inline = false, className }: FormattedTextProps) {
  if (!text) return null;

  if (inline) {
    return <>{formatInline(text.replace(/\s*\r?\n\s*/g, " "), "i", false)}</>;
  }

  const lines = text.replace(/\r\n?/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // ``` on its own opening line → a monospace block up to the closing ```.
    if (trimmed.startsWith("```") && trimmed.indexOf("```", 3) === -1) {
      let j = i + 1;
      while (j < lines.length && !lines[j].includes("```")) j++;
      if (j < lines.length) {
        const head = trimmed.slice(3);
        const tail = lines[j].slice(0, lines[j].indexOf("```"));
        const body = [...(head ? [head] : []), ...lines.slice(i + 1, j), ...(tail ? [tail] : [])];
        blocks.push(
          <pre key={`b${k++}`} className={PRE_CLASS}>
            {body.join("\n")}
          </pre>,
        );
        i = j + 1;
        continue;
      }
    }

    if (/^>\s?/.test(line)) {
      blocks.push(
        <span key={`b${k++}`} className={QUOTE_CLASS}>
          {formatInline(line.replace(/^>\s?/, ""), `q${k}`, true)}
        </span>,
      );
    } else {
      blocks.push(
        <span key={`b${k++}`} className="block">
          {/* A blank line keeps its height — the gaps are the structure. */}
          {line.length ? formatInline(line, `l${k}`, true) : " "}
        </span>,
      );
    }
    i++;
  }

  return <div className={cn("whitespace-pre-wrap break-words", className)}>{blocks}</div>;
}
